import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as v2Tokens from './studentThemeV2';
import { VISUAL_STYLE_PRESETS } from './themeTokens';

// ── 共享中性基础（所有浅色 academic 主题共用） ──
// 参考 Linear / Vercel 的克制配色：90% 中性 + 10% 强调色
const SHARED_LIGHT_BASE = {
  primary: '#18181B',       // zinc-900 文字黑
  background: '#FAFAF9',    // 暖近白（Linear 同款）
  surface: '#FFFFFF',       // 纯白卡片
  text: '#18181B',          // 主文字
  textSecondary: '#3F3F46', // zinc-700
  muted: '#71717A',         // zinc-500
  subtle: '#A1A1AA',        // zinc-400
  border: '#E6E7E9',        // 中性灰边框
  success: '#059669',       // emerald-600（比 #10B981 更成熟）
  warning: '#D97706',       // amber-600
  error: '#DC2626',         // red-600
  info: '#2563EB',          // blue-600
  shadow: '#000000',
};

const SHARED_DARK_BASE = {
  primary: '#F1F5F9',
  background: '#09090B',
  surface: '#18181B',
  surfaceTint: '#27272A',
  text: '#FAFAFA',
  textSecondary: '#D4D4D8',
  muted: '#71717A',
  subtle: '#52525B',
  border: '#27272A',
  success: '#34D399',
  warning: '#FBBF24',
  error: '#F87171',
  info: '#60A5FA',
  shadow: '#000000',
};

const themes = {
  ocean: {
    id: 'ocean',
    name: '海洋蓝',
    description: '冷静专注，经典蓝色',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_LIGHT_BASE,
      accent: '#3B6AF8',
      accentDark: '#2651E8',
      secondary: '#EEF2FF',
      surfaceTint: '#F4F6FB',
    },
    isDark: false,
  },
  forest: {
    id: 'forest',
    name: '森林绿',
    description: '沉稳自然，低饱和翡翠',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_LIGHT_BASE,
      accent: '#0D9373',
      accentDark: '#067A5C',
      secondary: '#ECFDF5',
      surfaceTint: '#F4FBF7',
    },
    isDark: false,
  },
  sunset: {
    id: 'sunset',
    name: '日落橙',
    description: '温暖克制，不会刺眼',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_LIGHT_BASE,
      accent: '#E8590C',
      accentDark: '#D13A0B',
      secondary: '#FFF7ED',
      surfaceTint: '#FDF7F4',
    },
    isDark: false,
  },
  rose: {
    id: 'rose',
    name: '玫瑰粉',
    description: '柔和典雅，低饱和玫瑰',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_LIGHT_BASE,
      accent: '#DB2777',
      accentDark: '#BE185D',
      secondary: '#FDF2F8',
      surfaceTint: '#FDF6F8',
    },
    isDark: false,
  },
  teal_light: {
    id: 'teal_light',
    name: '青瓷绿',
    description: '清雅如玉，学术气质',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_LIGHT_BASE,
      accent: '#0D9488',
      accentDark: '#0F766E',
      secondary: '#F0FDFA',
      surfaceTint: '#F4FBF9',
    },
    isDark: false,
  },
  midnight: {
    id: 'midnight',
    name: '午夜黑',
    description: '暗色护眼，深邃优雅',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_DARK_BASE,
      accent: '#818CF8',
      accentDark: '#6366F1',
      secondary: '#1E1B4B',
    },
    isDark: true,
  },
  teal_dark: {
    id: 'teal_dark',
    name: '青瓷暗夜',
    description: '沉静的暗色学术风格',
    visual: VISUAL_STYLE_PRESETS.academic,
    colors: {
      ...SHARED_DARK_BASE,
      accent: '#2DD4BF',
      accentDark: '#14B8A6',
      secondary: '#042F2E',
    },
    isDark: true,
  },
  mecha: {
    id: 'mecha',
    name: '机甲风暴',
    description: '霓虹电路，赛博朋克',
    visual: VISUAL_STYLE_PRESETS.mecha,
    colors: {
      primary: '#22D3EE',
      secondary: '#0B1120',
      accent: '#06B6D4',
      accentDark: '#0891B2',
      background: '#0A0E1A',
      surface: '#111827',
      surfaceTint: '#1F2937',
      text: '#E2E8F0',
      textSecondary: '#94A3B8',
      muted: '#64748B',
      subtle: '#475569',
      border: '#1E293B',
      success: '#34D399',
      warning: '#FBBF24',
      error: '#F87171',
      info: '#22D3EE',
      shadow: '#06B6D4',
    },
    isDark: true,
  },
  kawaii: {
    id: 'kawaii',
    name: '甜心可爱',
    description: '马卡龙色，软萌治愈',
    visual: VISUAL_STYLE_PRESETS.kawaii,
    colors: {
      primary: '#A855F7',
      secondary: '#F3E8FF',
      accent: '#F472B6',
      accentDark: '#EC4899',
      background: '#FFF5F9',
      surface: '#FFFFFF',
      surfaceTint: '#FCE7F3',
      text: '#4C1D95',
      textSecondary: '#6D28D9',
      muted: '#A78BFA',
      subtle: '#DDD6FE',
      border: '#F9A8D4',
      success: '#6EE7B7',
      warning: '#FDE68A',
      error: '#FCA5A5',
      info: '#93C5FD',
      shadow: '#E9D5FF',
    },
    isDark: false,
  },
  anime: {
    id: 'anime',
    name: '二次元',
    description: '高饱和纯色，动漫风格',
    visual: VISUAL_STYLE_PRESETS.anime,
    colors: {
      primary: '#2563EB',
      secondary: '#DBEAFE',
      accent: '#F43F5E',
      accentDark: '#E11D48',
      background: '#F8FAFC',
      surface: '#FFFFFF',
      surfaceTint: '#F1F5F9',
      text: '#0F172A',
      textSecondary: '#334155',
      muted: '#64748B',
      subtle: '#94A3B8',
      border: '#1E293B',
      success: '#22C55E',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
      shadow: '#0F172A',
    },
    isDark: false,
  },
  ink_wash: {
    id: 'ink_wash',
    name: '水墨丹青',
    description: '宣纸墨韵，朱砂点缀，传统国风',
    visual: VISUAL_STYLE_PRESETS.ink_wash,
    colors: {
      ...SHARED_LIGHT_BASE,
      accent: '#C23B2A',
      accentDark: '#8B1A1A',
      secondary: '#F0E6D8',
      surfaceTint: '#F7F2E9',
      background: '#F5F0E6',
      surface: '#FAF7F2',
      text: '#2C2416',
      textSecondary: '#5C5242',
      muted: '#8B7D6B',
      subtle: '#B0A590',
      border: '#D4C8B4',
      success: '#2D6A4F',
      warning: '#B8860B',
      error: '#C23B2A',
      info: '#3B6EA5',
      shadow: '#2C2416',
    },
    isDark: false,
  },
};

const ThemeContext = createContext(null);

const getStoredThemeId = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem('selectedTheme');
    }
    return null;
  } catch {
    return null;
  }
};

const setStoredThemeId = (themeId) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('selectedTheme', themeId);
    }
  } catch {
    // Ignore storage errors
  }
};

const getStoredEyeCare = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const v = window.localStorage.getItem('eyeCare');
      return v === 'on';
    }
    return false;
  } catch {
    return false;
  }
};

const setStoredEyeCare = (on) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('eyeCare', on ? 'on' : 'off');
    }
  } catch {
    // Ignore storage errors
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedThemeId = getStoredThemeId();
    if (savedThemeId && themes[savedThemeId]) {
      return themes[savedThemeId];
    }
    return themes.ocean;
  });

  const setTheme = useCallback((themeId) => {
    const theme = themes[themeId] || themes.ocean;
    setCurrentTheme(theme);
    setStoredThemeId(themeId);
  }, []);

  const [eyeCare, setEyeCareState] = useState(() => getStoredEyeCare());

  const toggleEyeCare = useCallback(() => {
    setEyeCareState((prev) => {
      const next = !prev;
      setStoredEyeCare(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedTheme') {
        const theme = themes[e.newValue] || themes.ocean;
        setCurrentTheme(theme);
      }
      if (e.key === 'eyeCare') {
        setEyeCareState(e.newValue === 'on');
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, themes, eyeCare, toggleEyeCare }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const getThemeColors = () => {
  try {
    const context = useContext(ThemeContext);
    if (context && context.currentTheme && context.currentTheme.colors) {
      return context.currentTheme.colors;
    }
    return themes.ocean.colors;
  } catch {
    return themes.ocean.colors;
  }
};

export const useTokens = () => {
  const { currentTheme } = useTheme();
  const visual = currentTheme.visual || VISUAL_STYLE_PRESETS.academic;
  return {
    colors: currentTheme.colors,
    spacing: v2Tokens.spacing,
    radius: v2Tokens.radius,
    shadows: v2Tokens.shadows,
    typography: v2Tokens.typography,
    glass: v2Tokens.glass,
    visual,
    isDark: currentTheme.isDark,
  };
};

export default ThemeContext;
export { themes };